/**
 * Checks if the current runtime environment is the client-side.
 */
export const isClient = typeof window !== 'undefined';

/**
 * Checks if a value is defined (not `undefined`).
 *
 * @param val - The value to check.
 * @returns `true` if the value is not `undefined`.
 */
export const isDef = <T = any>(val?: T): val is T => typeof val !== 'undefined';

/**
 * Asserts a condition is true, throws an error with provided info if not.
 *
 * @param condition - The condition to assert.
 * @param infos - Additional information to throw with the error if the assertion fails.
 */
export const assert = (condition: boolean, ...infos: any[]) => {
  if (!condition) throw new Error(...infos);
};

/**
 * Checks if a value is a boolean.
 *
 * @param val - The value to check.
 * @returns `true` if the value is a boolean.
 */
export const isBoolean = (val: any): val is boolean => typeof val === 'boolean';

/**
 * Checks if a value is a function.
 *
 * @param val - The value to check.
 * @returns `true` if the value is a function.
 */
export const isFunction = <T extends (...args: any[]) => any>(
  val: any
): val is T => typeof val === 'function';

/**
 * Checks if a value is a number.
 *
 * @param val - The value to check.
 * @returns `true` if the value is a number.
 */
export const isNumber = (val: any): val is number => typeof val === 'number';

/**
 * Checks if a value is a string.
 *
 * @param val - The value to check.
 * @returns `true` if the value is a string.
 */
export const isString = (val: unknown): val is string =>
  typeof val === 'string';

/**
 * Checks if a value is an object (and not null).
 *
 * @param val - The value to check.
 * @returns `true` if the value is an object.
 */
export const isObject = (val: any): val is object =>
  toString.call(val) === '[object Object]';

/**
 * Checks if a value is a Window object.
 *
 * @param val - The value to check.
 * @returns `true` if the value is a Window object.
 */
export const isWindow = (val: any): val is Window =>
  Object.prototype.toString.call(val) === '[object Window]';

/**
 * Returns the current timestamp in milliseconds.
 */
export const now = () => Date.now();

/**
 * Returns the current timestamp in milliseconds (alias for `Date.now()`).
 */
export const timestamp = () => +Date.now();

/**
 * Clamps a number between two values.
 *
 * @param n - The number to clamp.
 * @param min - The minimum value.
 * @param max - The maximum value.
 * @returns The clamped value.
 */
export const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

/**
 * A no-operation function. Does nothing when called.
 */
export const noop = () => {};

/**
 * Generates a random integer between two values (inclusive).
 *
 * @param min - The minimum value.
 * @param max - The maximum value.
 * @returns A random integer between `min` and `max`.
 */
export const rand = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Checks if the current environment is an iOS device.
 */
export const isIOS =
  isClient && /iP(ad|hone|od)/.test(window.navigator.userAgent);

/**
 * Checks if an object has its own property.
 *
 * @param val - The object to check.
 * @param key - The property name to check.
 * @returns `true` if the object has the property.
 */
export const hasOwn = <T extends object, K extends keyof T>(
  val: T,
  key: K
): key is K => Object.prototype.hasOwnProperty.call(val, key);
